// Posting (ERAs) — in-memory mock store. No backend.
import { useSyncExternalStore } from 'react';
import type {
  AutopilotRow,
  BankAccount,
  BankDifference,
  CardPayer,
  ClaimCandidate,
  EobCheck,
  EobDoc,
  FaxSetup,
  MatchedDeposit,
  Mode,
  Posting,
  PostingItem,
  QuestionEffect,
  UploadRecord,
} from './types';
import { buildDeltaCheck, buildDocuments, buildGuardianCheck, FAX_SETUP } from './eobFixtures';
import {
  AUTOPILOT,
  buildAutopilotRows,
  KIND_MINIMUM,
  seedTrainingKinds,
  type TrainingKind,
  buildAutopilotToPost,
  buildDifferences,
  buildPosted,
  buildToPost,
  JORGE_CANDIDATES,
  TRAINING,
} from './fixtures';
import { money, PO_ACTOR } from './utils';
import { lineForNetwork, isOon, referenceText } from './reference';
import { markPaidToPatientPosted } from '@/features/networks/paidToPatient';
import { getNetworkStatus, setNetworkStatus } from '@/features/networks/store';
import { hasMoneyPermission } from '@/features/shared/permissions';
import {
  attachStatements,
  buildFreshUploadDeposits,
  buildMatchedData,
  buildNewAccountDeposits,
  CHASE,
  FRESH_DONT_MATCH,
  matchedFromDifference,
  WELLS,
  ADMIN_NAME,
} from './matchedMock';
import { addAlreadyPostedRow, cardPayers, INITIAL_CARD_DISMISSALS, postedThisMonth, type PostedHistoryRow } from './historyMock';
import { longDate, nowClock, POSTING_TODAY_ISO, presetRange, shortDate, spanLabel } from './dates';
import { addDeniedFromPosting } from '../appeals/store';
import { PMS } from '@/features/shared/pms';

export const STEP_MS = 600;

type Scenario = '' | 'milestone' | 'autopilot' | 'clear' | 'no-candidate' | 'post-fails' | 'payer-wrong' | 'no-statement' | 'eobs' | 'eobs-autopilot' | 'smudge-disagree' | 'no-fax' | 'california';

interface Bank {
  differences: BankDifference[];
}

interface MatchedState {
  deposits: MatchedDeposit[];
  uploads: UploadRecord[];
  accounts: BankAccount[];
  /** The Oct 15–16 statement was read (a second "happy" upload is a duplicate). */
  freshRead: boolean;
}

export interface LogLine {
  id: string;
  /** "Today, 11:42 AM" */
  when: string;
  text: string;
}

interface State {
  mode: Mode;
  confirmed: number;
  /** Confirmed postings per kind of payment (training, H1 §6). */
  kinds: TrainingKind[];
  toPost: PostingItem[];
  posted: Posting[];
  bank: Bank;
  matched: MatchedState;
  /** Matches people unmatched, and what else they changed (kept in memory). */
  matchLog: LogLine[];
  /** Payer → the day someone marked "Done, I asked them" for the card fee card. */
  cardDismissals: Record<string, string>;
  autopilotRows: AutopilotRow[];
  scenario: Scenario;
  milestoneDismissed: boolean;
  // paper EOBs
  eobActive: boolean;
  documents: EobDoc[];
  checks: EobCheck[];
  faxSetup: FaxSetup | null;
}

const listeners = new Set<() => void>();
let version = 0;
function emit(): void {
  version += 1;
  for (const l of listeners) l();
}
const openedOnce = new Set<string>();
const who = `${PO_ACTOR.name} (${PO_ACTOR.org})`;
const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

function readParam(key: string): string {
  try {
    return new URLSearchParams(window.location.search).get(key) ?? '';
  } catch {
    return '';
  }
}
function readScenario(): Scenario {
  const raw = readParam('scenario');
  const ok: Scenario[] = ['milestone', 'autopilot', 'clear', 'no-candidate', 'post-fails', 'payer-wrong', 'no-statement', 'california'];
  return (ok as string[]).includes(raw) ? (raw as Scenario) : '';
}
export function isDevMode(): boolean {
  return readParam('dev') === '1';
}

function initState(): State {
  const scenario = readScenario();
  const toPost = buildToPost();
  const posted = buildPosted();
  const bank: Bank = { differences: buildDifferences() };
  const matched: MatchedState = { ...buildMatchedData(), freshRead: false };

  if (scenario === 'payer-wrong') {
    const mt = bank.differences.find((d) => d.id === 'BD-3');
    if (mt) {
      mt.check = { ok: false, text: 'MetLife paid the right amount in August. The $23.88 shouldn’t have been taken back.' };
      mt.choices = [
        { id: 'dispute', label: 'Dispute it with MetLife', effect: 'dispute', primary: true },
        { id: 'post', label: 'Post the takeback on Noah’s claim', effect: 'post_fix' },
      ];
    }
  }
  if (scenario === 'no-statement') {
    bank.differences = [];
    matched.deposits = [];
    matched.uploads = [];
    matched.accounts = [];
  }
  if (scenario === 'no-candidate') {
    const jorge = toPost.find((t) => t.id === 'PO-1');
    if (jorge) {
      if (jorge.compare) jorge.compare = jorge.compare.map((r) => ({ ...r, claim: '—', verdict: '', ok: true }));
      jorge.question = { text: 'Which claim is this?', options: [{ id: 'different', label: 'Search open Delta claims', effect: 'search' }] };
    }
  }

  const base: State = {
    mode: 'training',
    confirmed: TRAINING.confirmed,
    kinds: seedTrainingKinds(),
    toPost,
    posted,
    bank,
    matched,
    matchLog: [],
    cardDismissals: { ...INITIAL_CARD_DISMISSALS },
    autopilotRows: buildAutopilotRows(),
    scenario,
    milestoneDismissed: false,
    // Paper EOB checks are real To-post items (rcm-19 §8, D105): Document Center's
    // "Sent to Posting" link (/posting/{checkId}) resolves to these, and the tab
    // count includes them. The dedicated Paper-EOBs tab was retired.
    eobActive: true,
    documents: [],
    checks: [buildDeltaCheck(), buildGuardianCheck()],
    faxSetup: FAX_SETUP,
  };

  if (scenario === 'clear') return { ...base, toPost: [], checks: [], eobActive: false };
  if (scenario === 'milestone') return { ...base, confirmed: 99 };
  if (scenario === 'autopilot') {
    return { ...base, mode: 'autopilot', toPost: buildAutopilotToPost(), checks: [], eobActive: false, milestoneDismissed: true };
  }
  if (scenario === 'eobs' || scenario === 'eobs-autopilot' || scenario === 'smudge-disagree' || scenario === 'no-fax') {
    const delta = buildDeltaCheck();
    const guardian = buildGuardianCheck();
    if (scenario === 'smudge-disagree') {
      const isaac = delta.claims.find((c) => c.id === 'cl-isaac');
      if (isaac) {
        isaac.status = 'needs_you';
        isaac.statusNote = 'Which amount is on the EOB?';
        isaac.smudgeValues = [
          { value: 854, source: 'the check total minus the other claims' },
          { value: 584, source: 'the allowed amount × the plan’s percentage' },
          { value: 854, source: 'Delta’s portal' },
        ];
      }
    }
    return {
      ...base,
      eobActive: true,
      documents: buildDocuments(),
      checks: [delta, guardian],
      faxSetup: scenario === 'no-fax' ? null : FAX_SETUP,
    };
  }
  return base;
}

const state: State = initState();

// ---- subscription ----
export function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
export function getVersion(): number {
  return version;
}
export function usePostingVersion(): number {
  return useSyncExternalStore(subscribe, getVersion, getVersion);
}

// ---- getters ----
export function getScenarioSync(): Scenario {
  return state.scenario;
}
export function getModeSync(): Mode {
  return state.mode;
}
export function getConfirmedSync(): number {
  return state.confirmed;
}
/** Each kind of payment and how many a person confirmed (5 needed before autopilot covers it). */
export function getTrainingKindsSync(): TrainingKind[] {
  return state.kinds;
}
/** The kinds still below their minimum, in a sentence: "takebacks and payments after a claim was closed". */
export function kindsBelowMinimumSync(): string[] {
  return state.kinds.filter((k) => k.confirmed < KIND_MINIMUM).map((k) => k.label);
}
export function getAutopilotMeta(): { since: string; by: string } {
  return { since: state.scenario === 'autopilot' ? AUTOPILOT.since : 'today', by: AUTOPILOT.by };
}
export function getToPostSync(): PostingItem[] {
  return state.toPost.filter((t) => !t.removed);
}
export function getItemSync(id: string): PostingItem | null {
  return state.toPost.find((t) => t.id === id) ?? null;
}
export function groupedToPost(): { needsYou: PostingItem[]; ready: PostingItem[]; checking: PostingItem[] } {
  const items = getToPostSync();
  const byEra = (a: PostingItem, b: PostingItem): number => a.eraDate.localeCompare(b.eraDate);
  return {
    needsYou: items.filter((t) => t.group === 'needs_you').sort(byEra),
    ready: state.mode === 'autopilot' ? [] : items.filter((t) => t.group === 'ready').sort(byEra),
    // D238: ELVA is checking the PMS again; nothing for a person to do. They stay in To post and in its count.
    checking: items.filter((t) => t.group === 'checking').sort(byEra),
  };
}
export function getPostedSync(): Posting[] {
  return state.posted;
}
export function getPostingSync(id: string): Posting | null {
  return state.posted.find((p) => p.id === id) ?? null;
}
export function getBankSync(): Bank {
  return state.bank;
}
export function getDifferencesSync(): BankDifference[] {
  return state.bank.differences.filter((d) => !d.removed && !d.posted);
}
export function getDifferenceSync(id: string): BankDifference | null {
  return state.bank.differences.find((d) => d.id === id) ?? null;
}
export function getAutopilotRowsSync(): AutopilotRow[] {
  return state.autopilotRows;
}

export function toPostCountSync(): number {
  return getToPostSync().length + getChecksSync().length;
}
export function toPostTotalSync(): number {
  return getToPostSync().reduce((s, t) => s + t.amount, 0) + getChecksSync().reduce((s, c) => s + c.amount, 0);
}
export function postedMonthCountSync(): number {
  return state.mode === 'autopilot' ? AUTOPILOT.postedMonth : postedThisMonth().count;
}
export function bankDiffCountSync(): number {
  return getDifferencesSync().length;
}
/**
 * One count source (D204): the sidebar Posting badge = what the band calls "To post"
 * (ERAs + paper checks) plus the bank deposits that don't match, the two numbers
 * the band and the Bank deposits tab show.
 */
export function postingCountSync(): number {
  return toPostCountSync() + bankDiffCountSync();
}
export function usePostingCount(): number {
  return useSyncExternalStore(subscribe, postingCountSync, postingCountSync);
}

/** Needs-you items need an answer before Post is enabled. Ready items post straight away. */
export function canPost(item: PostingItem): boolean {
  // D238/D269: a posting ELVA couldn't confirm in the PMS never posts again without a person's override.
  if (item.pmsCheck) return Boolean(item.override);
  // D291: held until the dentist's network status with the payer is confirmed (here or in Settings › Networks).
  if (item.networkQ) {
    if (getNetworkStatus(item.networkQ.dentist, item.networkQ.payer) !== 'unconfirmed') return true;
    return item.answered?.effect === 'enable_post';
  }
  if (item.group === 'ready') return true;
  if (item.answered?.effect === 'enable_post') return true;
  if (item.returnChoice) return true; // not-our-patient confirmed path
  return false;
}

function pushHistory(item: PostingItem, entry: string): void {
  item.history = [...item.history, entry];
}

export function touchOpen(id: string): void {
  const item = getItemSync(id);
  if (item && !openedOnce.has(id) && !item.removed) {
    openedOnce.add(id);
    emit();
  }
}

// ---- answers ----
export function answerItem(id: string, choice: string, extra?: { note?: string }): void {
  const item = state.toPost.find((t) => t.id === id);
  if (!item) return;
  const opt = item.question?.options.find((o) => o.id === choice);
  if (!opt) return;
  const effect: QuestionEffect = opt.effect;
  if (effect === 'appeal' || effect === 'dispute') {
    item.removed = effect === 'appeal' ? 'appealed' : 'disputed';
    addDeniedFromPosting({ id: `PO-${effect.toUpperCase()}-${item.id}`, patient: item.patient, firstName: item.firstName, payer: item.payer, memberId: item.memberId, reason: effect === 'appeal' ? 'Paid as a cheaper procedure' : 'Takeback disputed' });
    emit();
    return;
  }
  if (effect === 'something_wrong') {
    pushHistory(item, `flagged by ${who}: ${extra?.note ?? ''}`.trim());
    emit();
    return;
  }
  if (effect === 'pms_there') {
    // D249: it is already in the PMS. ELVA links it, posts nothing, and it shows in Posted as Already posted (D271).
    item.removed = 'linked';
    pushHistory(item, `${who} said it’s there in ${PMS}; ELVA linked it and didn’t post again`);
    addAlreadyPostedRow({ id: item.id, patient: item.patient, payer: item.payer.name, amount: item.amount, trace: item.trace });
    emit();
    return;
  }
  if (effect === 'pms_not_there' && item.pmsCheck) {
    // D269: what the person saw, not proof. ELVA checks the PMS once more before anything else.
    pushHistory(item, `${who} saw no payment in ${PMS} (recorded as what they saw, not as proof)`);
    item.pmsCheck = { stage: 'rechecking', checks: item.pmsCheck.checks, seen: 'not_there' };
    emit();
    setTimeout(() => {
      if (!item.pmsCheck || item.removed) return;
      item.pmsCheck = { stage: 'unconfirmed', checks: item.pmsCheck.checks + 1, seen: 'not_there' };
      pushHistory(item, `ELVA checked ${PMS} once more and still can’t confirm`);
      emit();
    }, 1800);
    return;
  }
  if ((effect === 'network_in' || effect === 'network_out') && item.networkQ) {
    // D291: the answer updates Settings › Practice details › Networks (logged) and the posting continues.
    const status = effect === 'network_in' ? 'in' : 'out';
    setNetworkStatus(item.networkQ.dentist, item.networkQ.payer, { status }, PO_ACTOR.name, PO_ACTOR.org);
    pushHistory(item, `${who} confirmed ${item.networkQ.dentist} is ${status === 'in' ? 'in' : 'out of'} network with ${item.networkQ.payer}`);
    item.answered = { choice, effect: 'enable_post' };
    emit();
    return;
  }
  item.answered = { choice, effect };
  emit();
}

/** D269: "Post it again anyway" — only with the override permission, and it needs a one-line reason. */
export function approvePostAgain(id: string, reason: string): boolean {
  const item = state.toPost.find((t) => t.id === id);
  const why = reason.trim();
  if (!item?.pmsCheck || !why || !hasMoneyPermission()) return false;
  item.override = { reason: why };
  pushHistory(item, `${who} approved posting it again anyway: “${why}”`);
  emit();
  return true;
}

export function changeAnswer(id: string): void {
  const item = state.toPost.find((t) => t.id === id);
  if (!item) return;
  delete item.answered;
  delete item.returnChoice;
  emit();
}

export function setReturnChoice(id: string, choice: string): void {
  const item = state.toPost.find((t) => t.id === id);
  if (!item) return;
  item.returnChoice = choice;
  emit();
}

export function searchClaims(itemId: string, query: string): ClaimCandidate[] {
  const item = getItemSync(itemId);
  const q = query.trim().toLowerCase();
  const all = itemId === 'PO-1' ? JORGE_CANDIDATES : [];
  const mine = all.filter((c) => c.patient === item?.patient);
  const pool = mine.length > 0 ? mine.concat(all.filter((c) => c.patient !== item?.patient)) : all;
  if (!q) return pool;
  return pool.filter((c) => c.patient.toLowerCase().includes(q) || c.claimNumber.includes(q) || c.procedure.toLowerCase().includes(q));
}

export function pickClaim(itemId: string, candidateId: string): void {
  const item = state.toPost.find((t) => t.id === itemId);
  const cand = JORGE_CANDIDATES.find((c) => c.id === candidateId);
  if (!item || !cand) return;
  const sameClaim = cand.serviceDate.startsWith('Sep 22') && cand.patient === item.patient;
  item.compare = [
    { label: 'Patient', era: 'J MARTINEZ', claim: cand.patient, verdict: cand.patient === item.patient ? 'Same' : 'Different patient', ok: cand.patient === item.patient },
    { label: 'Visit', era: 'Sep 22, 2026', claim: cand.serviceDate, verdict: cand.serviceDate.startsWith('Sep 22') ? 'Same' : 'Different date', ok: cand.serviceDate.startsWith('Sep 22') },
    { label: 'Procedure', era: 'D2740, tooth #3', claim: cand.procedure, verdict: cand.procedure.startsWith('D2740') ? 'Same' : 'Different procedure', ok: cand.procedure.startsWith('D2740') },
    { label: 'Claim number', era: '4471', claim: cand.claimNumber, verdict: 'Found', ok: true },
  ];
  delete item.answered;
  item.question = {
    text: `Is this ${item.firstName}’s claim of ${cand.serviceDate.replace(', 2026', '')}?`,
    options: [
      { id: 'yes', label: 'Yes, it’s this claim', effect: sameClaim ? 'enable_post' : 'enable_post' },
      { id: 'different', label: 'No, it’s a different claim', effect: 'search' },
      { id: 'not_ours', label: 'Not our patient', effect: 'not_our_patient' },
    ],
  };
  emit();
}

// ---- posting flow (§4.5) ----
function itemToPosting(item: PostingItem): Posting {
  const oon = isOon(item);
  const special = item.kind === 'paid_to_patient' || item.kind === 'patient_credit';
  // Out of network: nothing is posted as a write-off, so the patient's side grows by it (D196).
  const lines = item.lines.map((l) => lineForNetwork(l, oon));
  const writtenOffBefore = item.lines.reduce((t, l) => t + l.writeOff, 0);
  return {
    id: `POSTED-${item.id}`,
    patient: item.patient,
    firstName: item.firstName,
    payer: item.payer,
    memberId: item.memberId,
    claimOf: item.claimOf ? `claim of ${item.claimOf}` : (item.compare?.find((c) => c.label === 'Visit')?.claim ?? 'this claim'),
    dentist: item.dentist,
    payment: item.payment,
    kind: item.kind,
    ...(item.paidToPatient ? { paidToPatient: item.paidToPatient } : {}),
    amount: item.amount,
    amountLabel: item.amountLabel,
    postedTime: '10:42 AM',
    postedDateLabel: 'Today',
    postedIso: '2026-10-16T10:42:00',
    postedDay: 'today',
    eraDisplay: item.eraDisplay.replace(', 2026', ''),
    trace: item.trace,
    confirmedBy: PO_ACTOR,
    how: item.how,
    lines,
    balanceBefore: item.balanceBefore,
    balanceAfter: oon && !special ? Math.round((item.balanceAfter + writtenOffBefore) * 100) / 100 : item.balanceAfter,
    howFound: item.whatHappened.body || item.whatHappened.headline,
    sources: item.sources,
    log: item.log,
    durationSec: item.durationSec,
    depositNote: item.depositNote ?? 'No bank statement covers this date yet.',
    statementCovers: Boolean(item.depositNote),
    history: [...item.history, `confirmed by ${who} today`, 'posted by ELVA 10:42 AM', 'checked 10:42 AM'],
  };
}

/** The claim leaves Claims' "payer pays the patient" group once the $0.00 posting is in (D197). */
function closePaidToPatient(item: PostingItem): void {
  if (item.kind === 'paid_to_patient' && item.claimId) markPaidToPatientPosted(item.claimId);
}

/** Postings where the payer paid the patient, as Posted rows (the "Paid to the patient" filter). */
export function getPaidToPatientRowsSync(): PostedHistoryRow[] {
  return state.posted
    .filter((p) => p.kind === 'paid_to_patient' && !p.isUndo && !p.undone)
    .map((p) => ({
      id: p.id,
      patient: p.patient,
      payer: p.payer.name,
      amount: p.amount,
      kind: 'Insurance payment' as const,
      dateIso: p.postedIso.slice(0, 10),
      dateLabel: longDate(p.postedIso.slice(0, 10)),
      trace: '',
      claimNumber: '',
      confirmedBy: p.confirmedBy === 'self' ? 'Posted by your team' : p.confirmedBy === 'autopilot' ? 'Autopilot' : p.confirmedBy.name,
      method: 'eft' as const,
      reference: referenceText(p.payment, p.payer.short, p.trace),
      paidToPatient: true,
      ...(p.paidToPatient ? { note: `${p.payer.short} sent ${money(p.paidToPatient.amount)} to ${p.firstName}` } : {}),
    }));
}

/** Drives the live step line (§4.5). Yields each step, then done or failed. */
export async function* postItem(id: string): AsyncGenerator<{ step: string } | { done: true } | { failed: string }> {
  const item = state.toPost.find((t) => t.id === id);
  if (!item) return;
  for (const s of item.log) {
    yield { step: s.text };
    await wait(STEP_MS);
  }
  if (state.scenario === 'post-fails' && id === 'PO-1') {
    yield { failed: `ELVA posted it, but ${PMS} shows Jorge’s balance as $972.00, not $486.00. ELVA reversed the posting. Nothing changed in ${PMS}.` };
    return;
  }
  // success
  item.removed = 'posted';
  state.posted.unshift(itemToPosting(item));
  state.confirmed += 1;
  const kindRow = state.kinds.find((k) => k.id === item.kind);
  if (kindRow) kindRow.confirmed += 1;
  closePaidToPatient(item);
  emit();
  yield { done: true };
}

export function postItMyself(id: string): void {
  const item = state.toPost.find((t) => t.id === id);
  if (!item) return;
  item.removed = 'self';
  closePaidToPatient(item);
  const p = itemToPosting(item);
  p.confirmedBy = 'self';
  p.history = [...item.history, `posted by ${PO_ACTOR.name} in ${PMS}`];
  state.posted.unshift(p);
  emit();
}

export function recordLearning(_id: string, _verdict: 'right' | 'one-off'): void {
  emit();
}

/** Not-our-patient: confirm returning the money; the item leaves To post. */
export function confirmReturn(id: string): void {
  const item = state.toPost.find((t) => t.id === id);
  if (!item) return;
  item.removed = 'self';
  emit();
}

// ---- Posted / undo ----
export async function* undoPosting(id: string): AsyncGenerator<{ step: string } | { done: true }> {
  const p = state.posted.find((x) => x.id === id);
  if (!p) return;
  const steps = [`Opened the claim in ${PMS}`, 'Reversed the insurance payment', 'Checked: the payment is removed and the balance is restored'];
  for (const s of steps) {
    yield { step: s };
    await wait(STEP_MS);
  }
  p.undone = true;
  p.reverseNote = `Undone today at 10:50 AM. ELVA reversed it in ${PMS} and put the $${Math.abs(p.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })} back in To post.`;
  state.posted.unshift({
    ...p,
    id: `UNDO-${p.id}`,
    isUndo: true,
    undone: false,
    postedTime: '10:50 AM',
    postedDateLabel: 'Today',
    postedIso: '2026-10-16T10:50:00',
    postedDay: 'today',
    reverseNote: `Reversal of ${p.firstName}’s $${Math.abs(p.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })} posting.`,
  });
  // return to To post
  const original = buildToPost().find((t) => t.patient === p.patient);
  if (original && !state.toPost.some((t) => t.patient === p.patient && !t.removed)) {
    state.toPost.unshift({ ...original, id: `REOPEN-${p.id}` });
  }
  emit();
  yield { done: true };
}

// ---- Bank ----
function addMatched(dep: MatchedDeposit): void {
  const [withFile] = attachStatements([dep], state.matched.uploads);
  state.matched.deposits.unshift(withFile ?? dep);
}

export function answerDifference(id: string, choice: string): void {
  const d = state.bank.differences.find((x) => x.id === id);
  if (!d) return;
  const opt = d.choices.find((c) => c.id === choice);
  if (!opt) return;
  if (opt.effect === 'dispute') {
    d.removed = true;
    addDeniedFromPosting({ id: `PO-BANKDISPUTE-${id}`, patient: 'Noah Kim', firstName: 'Noah', payer: { name: 'MetLife', short: 'MetLife' }, memberId: '—', reason: 'Takeback disputed' });
    emit();
    return;
  }
  if (opt.effect === 'yes_account') {
    d.removed = true;
    d.answer = { choice, note: 'ELVA will ask for that account’s statement.' };
    emit();
    return;
  }
  if (opt.effect === 'no_account') {
    d.removed = true;
    d.answer = { choice, note: 'ELVA is calling Cigna to stop and resend the payment, and is preparing the bank-details form for signing.' };
    emit();
    return;
  }
  if (opt.effect === 'rematch') {
    if (d.matchedId) undoUnmatch(d.matchedId);
    return;
  }
  if (opt.effect === 'pick_a' || opt.effect === 'pick_b') {
    const dep = matchedFromDifference(id, opt.effect === 'pick_a' ? 'a' : 'b');
    d.removed = true;
    d.answer = { choice, note: 'Matched. It moved to Matched.' };
    if (dep) addMatched(dep);
    emit();
    return;
  }
  if (opt.effect === 'self') {
    d.removed = true;
    d.answer = {
      choice,
      note:
        choice === 'wrong_payment'
          ? 'ELVA will look for the right payment and ask you to confirm it.'
          : choice === 'not_insurance'
            ? 'ELVA took it off the insurance deposits.'
            : 'You’ll post it yourself.',
    };
    emit();
  }
}

/** Post the takeback/interest fix: plays steps, then moves the deposit to Matched. */
export async function* postDifference(id: string): AsyncGenerator<{ step: string } | { done: true }> {
  const d = state.bank.differences.find((x) => x.id === id);
  if (!d) return;
  for (const s of d.log ?? []) {
    yield { step: s.text };
    await wait(STEP_MS);
  }
  d.posted = true;
  d.answer = { choice: 'post', note: 'Posted and checked. Moved to Matched.' };
  const dep = matchedFromDifference(id);
  if (dep) addMatched(dep);
  emit();
  yield { done: true };
}

// ---- Matched deposits (rcm-32) ----
export function getMatchedDepositsSync(): MatchedDeposit[] {
  return state.matched.deposits.filter((d) => !d.unmatched);
}
export function getMatchedDepositSync(id: string): MatchedDeposit | null {
  return state.matched.deposits.find((d) => d.id === id) ?? null;
}
export function getUploadsSync(): UploadRecord[] {
  return [...state.matched.uploads].sort((a, b) => (a.toIso === b.toIso ? (a.uploadedIso < b.uploadedIso ? 1 : -1) : a.toIso < b.toIso ? 1 : -1));
}
export function getAccountsSync(): BankAccount[] {
  return state.matched.accounts;
}
export function getMatchLogSync(): LogLine[] {
  return state.matchLog;
}

/** Matched deposits dated this month: the Matched tab count. */
export function matchedMonthCountSync(): number {
  const r = presetRange('this-month');
  return getMatchedDepositsSync().filter((d) => (r.from ? d.dateIso >= r.from : true) && (r.to ? d.dateIso <= r.to : true)).length;
}

export interface Coverage {
  from: string;
  to: string;
  accounts: BankAccount[];
  holes: { from: string; to: string }[];
}
/** What the uploaded statements cover, and any days with no statement. */
export function getCoverageSync(): Coverage | null {
  const ups = [...state.matched.uploads].sort((a, b) => (a.fromIso < b.fromIso ? -1 : 1));
  const first = ups[0];
  if (!first) return null;
  const holes: { from: string; to: string }[] = [];
  let end = first.toIso;
  let last = first.toIso;
  for (const u of ups.slice(1)) {
    if (u.fromIso > addDaysIso(end, 1)) holes.push({ from: addDaysIso(end, 1), to: addDaysIso(u.fromIso, -1) });
    if (u.toIso > end) end = u.toIso;
    if (u.toIso > last) last = u.toIso;
  }
  return { from: first.fromIso, to: last, accounts: state.matched.accounts, holes };
}
function addDaysIso(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function accountOf(id: string): BankAccount {
  return state.matched.accounts.find((a) => a.id === id) ?? CHASE;
}

/** "This isn't right": unmatch the deposit and put it in Bank deposits (logged, undoable). */
export function unmatchDeposit(id: string): boolean {
  const dep = state.matched.deposits.find((d) => d.id === id);
  if (!dep || dep.unmatched) return false;
  dep.unmatched = true;
  const time = nowClock();
  const what = dep.kind === 'checks' ? 'check deposit' : `${dep.payer} deposit`;
  state.bank.differences.unshift({
    id: `UM-${id}`,
    kind: 'unmatched',
    payer: dep.payer,
    diff: dep.amount,
    dateLine: `${shortDate(dep.dateIso)} deposit · ${accountOf(dep.accountId).short}`,
    headline: `You said the ${what} was matched wrong`,
    body: `ELVA moved this ${money(dep.amount)} deposit from Matched back to Bank deposits. Tell ELVA what is wrong and it looks again.`,
    prompt: 'What’s wrong with this match?',
    choices: [
      { id: 'rematch', label: 'Nothing, match it again', effect: 'rematch', primary: true },
      { id: 'wrong_payment', label: 'It belongs to a different payment', effect: 'self' },
      { id: 'not_insurance', label: 'It isn’t an insurance deposit', effect: 'self' },
    ],
    matchedId: id,
    loggedLine: `Unmatched by ${ADMIN_NAME} today at ${time}. The match can be undone.`,
  });
  state.matchLog.unshift({ id: `ml-${id}-${String(state.matchLog.length)}`, when: `Today, ${time}`, text: `${ADMIN_NAME} unmatched the ${money(dep.amount)} ${what} of ${shortDate(dep.dateIso)}.` });
  emit();
  return true;
}

/** Undo "This isn't right" (or answer "match it again"). */
export function undoUnmatch(id: string): void {
  const dep = state.matched.deposits.find((d) => d.id === id);
  if (!dep?.unmatched) return;
  dep.unmatched = false;
  const row = state.bank.differences.find((d) => d.id === `UM-${id}`);
  if (row) row.removed = true;
  state.matchLog.unshift({ id: `ml-${id}-${String(state.matchLog.length)}`, when: `Today, ${nowClock()}`, text: `${ADMIN_NAME} matched the ${money(dep.amount)} ${dep.kind === 'checks' ? 'check deposit' : `${dep.payer} deposit`} of ${shortDate(dep.dateIso)} again.` });
  emit();
}

// ---- Upload bank statement (the band button) ----
export type UploadReading =
  | { status: 'error'; message: string }
  | { status: 'new_account'; account: BankAccount; lines: number }
  | { status: 'done'; message: string; hasMismatch: boolean };

function hasColumns(header: string): boolean {
  const h = header.toLowerCase();
  return /date|posted/.test(h) && /desc|memo|detail|narrative|payee|name/.test(h) && /amount|credit|debit|money in|money out|deposit/.test(h);
}
function lineCount(text: string): number {
  const rows = text.split(/\r?\n/).filter((l) => l.trim() !== '');
  const n = Math.max(0, rows.length - 1);
  // A short sample file still reads like a real statement in the demo.
  return n >= 40 ? n : 96;
}

/**
 * Reads one file's text (the demo does it in the browser). The first error is a
 * real extension check. Filename hints demo the others: "nocols" (no recognisable
 * columns), "dupe" (lines already uploaded), "newacct" (a new account). Any other
 * CSV with date, description and amount columns is read once; the next one is a
 * duplicate because the statement now covers those days.
 */
export function readStatement(fileName: string, text: string): UploadReading {
  const lower = fileName.toLowerCase();
  if (!lower.endsWith('.csv')) {
    return { status: 'error', message: "This file isn't a CSV. Export the statement from your bank as CSV." };
  }
  const lines = lineCount(text);
  const noCols = { status: 'error', message: "ELVA couldn't find the date, description and amount columns." } as const;
  if (lower.includes('nocols')) return noCols;
  const dup = (n: number): UploadReading => ({ status: 'error', message: `${String(n)} lines were already uploaded and were skipped.` });
  if (lower.includes('dupe')) return dup(Math.min(112, lines));
  if (lower.includes('newacct')) {
    if (state.matched.accounts.some((a) => a.id === WELLS.id)) return dup(lines);
    return { status: 'new_account', account: WELLS, lines };
  }
  const header = text.split(/\r?\n/).find((l) => l.trim() !== '') ?? '';
  if (!hasColumns(header)) return noCols;
  if (state.matched.freshRead) return dup(lines);

  // Read it: Chase ··4417, Oct 15–16. One deposit matches, one doesn't.
  const fresh = buildFreshUploadDeposits();
  const freshTotal = fresh.reduce((t, d) => t + d.amount, 0);
  const total = Math.round((freshTotal + FRESH_DONT_MATCH.amount) * 100) / 100;
  const up: UploadRecord = {
    id: 'UP-fresh',
    fileName,
    accountId: CHASE.id,
    fromIso: '2026-10-15',
    toIso: '2026-10-16',
    linesRead: lines,
    kept: fresh.length + 1,
    keptTotal: total,
    matched: fresh.length,
    dontMatch: 1,
    uploadedBy: ADMIN_NAME,
    uploadedIso: POSTING_TODAY_ISO,
    uploadedLabel: `${shortDate(POSTING_TODAY_ISO)}, ${nowClock()}`,
  };
  state.matched.uploads.push(up);
  for (const d of fresh) addMatched(d);
  state.bank.differences.push({
    id: FRESH_DONT_MATCH.id,
    kind: 'money_no_post',
    payer: 'Aetna',
    diff: FRESH_DONT_MATCH.amount,
    dateLine: 'Oct 16 deposit · Chase ··4417',
    headline: 'The money is in the bank, but nothing was posted for it',
    body: `A deposit of ${money(FRESH_DONT_MATCH.amount)} from Aetna landed Oct 16 with no ERA posted.`,
    handled: {
      ticks: [
        { text: 'Checked Aetna’s provider portal today: no remittance for this deposit yet.' },
        { text: 'ELVA checks again every morning and puts the claims in To post when it finds them.' },
      ],
    },
    choices: [],
  });
  state.matched.freshRead = true;
  emit();
  return {
    status: 'done',
    hasMismatch: true,
    message: `Read ${String(lines)} lines from ${CHASE.short}, ${spanLabel('2026-10-15', '2026-10-16')}. Kept ${String(up.kept)} insurance deposits (${money(total)}): ${String(fresh.length)} matched, 1 doesn't match.`,
  };
}

/** "Add it": adds the new account and reads its statement. */
export function addAccountAndRead(fileName: string, lines: number): string {
  const deposits = buildNewAccountDeposits();
  const total = Math.round(deposits.reduce((t, d) => t + d.amount, 0) * 100) / 100;
  if (!state.matched.accounts.some((a) => a.id === WELLS.id)) state.matched.accounts.push(WELLS);
  state.matched.uploads.push({
    id: 'UP-wells',
    fileName,
    accountId: WELLS.id,
    fromIso: '2026-10-01',
    toIso: '2026-10-14',
    linesRead: lines,
    kept: deposits.length,
    keptTotal: total,
    matched: deposits.length,
    dontMatch: 0,
    uploadedBy: ADMIN_NAME,
    uploadedIso: POSTING_TODAY_ISO,
    uploadedLabel: `${shortDate(POSTING_TODAY_ISO)}, ${nowClock()}`,
  });
  for (const d of deposits) addMatched(d);
  emit();
  return `Read ${String(lines)} lines from ${WELLS.short}, ${spanLabel('2026-10-01', '2026-10-14')}. Kept ${String(deposits.length)} insurance deposits (${money(total)}): ${String(deposits.length)} matched, 0 don't match.`;
}

// ---- card payments (Switch to free EFT) ----
export function getCardPayersSync(): CardPayer[] {
  return cardPayers(state.cardDismissals);
}
/** "Done, I asked them": hides the payer's card. It returns if a card payment arrives 30 days later. */
export function dismissCardPayer(payer: string): void {
  state.cardDismissals = { ...state.cardDismissals, [payer]: POSTING_TODAY_ISO };
  emit();
}
export function getCardDismissalsSync(): Record<string, string> {
  return state.cardDismissals;
}

// ---- mode / milestone ----
export function setMode(mode: Mode): void {
  state.mode = mode;
  if (mode === 'autopilot') {
    state.toPost = buildAutopilotToPost();
  }
  state.milestoneDismissed = true;
  emit();
}
export function dismissMilestone(): void {
  state.milestoneDismissed = true;
  emit();
}
export function shouldShowMilestone(): boolean {
  return state.confirmed >= TRAINING.milestone && !state.milestoneDismissed && state.mode === 'training';
}
/** The quiet "Turn on autopilot" header link shows only after the milestone was reached and dismissed. */
export function milestoneLinkVisible(): boolean {
  return state.mode === 'training' && state.milestoneDismissed && state.confirmed >= TRAINING.milestone;
}

// ---- dev ----
export function simulateNewEra(): void {
  const tmpl = buildToPost().find((b) => b.id === 'PO-4');
  if (!tmpl) return;
  const n: PostingItem = {
    ...tmpl,
    id: `DEV-${String(Date.now())}`,
    patient: 'Dev Patient',
    firstName: 'Dev',
    payer: { name: 'Delta Dental of Texas', short: 'Delta' },
    amount: 122,
    amountLabel: 'Delta paid',
    listMeta: 'ERA of Oct 15 · today',
    reason: 'Nothing unusual',
  };
  state.toPost.unshift(n);
  emit();
}

// ---- Paper EOBs ----
export function getEobActiveSync(): boolean {
  return state.eobActive;
}
export function getDocumentsSync(): EobDoc[] {
  return state.documents;
}
export function getChecksSync(): EobCheck[] {
  return state.checks.filter((c) => !c.allPosted);
}
export function getCheckSync(id: string): EobCheck | null {
  return state.checks.find((c) => c.id === id) ?? null;
}
export function getFaxSetupSync(): FaxSetup | null {
  return state.faxSetup;
}
/** Paper EOBs tab count = checks with a claim that needs a person. */
export function eobNeedsYouCountSync(): number {
  return getChecksSync().filter((c) => c.claims.some((cl) => cl.status === 'needs_you')).length;
}
export function checksForGroup(group: 'needs_you' | 'ready'): EobCheck[] {
  if (!state.eobActive) return [];
  return getChecksSync().filter((c) => c.group === group);
}

/** Post one claim on a check (plays steps); opens the next unposted when done. */
export async function* postCheckClaim(checkId: string, claimId: string): AsyncGenerator<{ step: string } | { done: true } | { allPosted: true }> {
  const check = state.checks.find((c) => c.id === checkId);
  const claim = check?.claims.find((cl) => cl.id === claimId);
  if (!check || !claim) return;
  const steps = [`Opened the ${PMS} batch for ${check.payer} check ${check.checkNumber}`, `Posted ${claim.patient}’s claim`, 'Checked: the claim shows Received'];
  for (const s of steps) {
    yield { step: s };
    await wait(STEP_MS);
  }
  claim.status = 'posted';
  claim.statusNote = 'Posted ✓ 10:14 AM';
  state.confirmed += 1;
  emit();
  if (check.claims.every((cl) => cl.status === 'posted')) {
    check.allPosted = true;
    emit();
    yield { allPosted: true };
    return;
  }
  yield { done: true };
}

export function answerCheckClaim(checkId: string, claimId: string, choice: string): void {
  const check = state.checks.find((c) => c.id === checkId);
  const claim = check?.claims.find((cl) => cl.id === claimId);
  if (!claim) return;
  const opt = claim.question?.options.find((o) => o.id === choice);
  if (opt?.effect === 'enable_post') {
    claim.answered = { choice, effect: 'enable_post' };
  } else if (opt?.effect === 'not_our_patient') {
    claim.answered = { choice, effect: 'not_our_patient' };
  }
  emit();
}

export function markAsEob(docId: string): void {
  const doc = state.documents.find((d) => d.id === docId);
  if (!doc) return;
  doc.badge = { label: 'In To post', tone: 'info' };
  doc.meta = 'ELVA is reading it again.';
  doc.actions = [{ label: 'Open', kind: 'open' }];
  emit();
}

export function saveFaxSetup(senders: string): void {
  state.faxSetup = { address: FAX_SETUP.address, senders, setUpBy: `${PO_ACTOR.name} (${PO_ACTOR.org})`, setUpWhen: 'today' };
  emit();
}
